import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  // Strike 2 — multi-tenant workspaces
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(() => {
    try {
      return localStorage.getItem("active_workspace_id") || null;
    } catch {
      return null;
    }
  });
  // Strike 3 — Admin "Enterprise Mode". When OFF, an admin sees the restricted
  // single-tenant UI (no workspace switcher / enterprise chrome). Persisted
  // per-browser. Default ON so admins keep the full agency UI.
  const [enterpriseMode, setEnterpriseMode] = useState(() => {
    try {
      const v = localStorage.getItem("admin_enterprise_mode");
      return v === null ? true : v === "true";
    } catch {
      return true;
    }
  });

  const toggleEnterpriseMode = useCallback(() => {
    setEnterpriseMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("admin_enterprise_mode", String(next));
        // OFF → drop back to the default workspace to fully simulate single-tenant.
        if (!next) localStorage.removeItem("active_workspace_id");
      } catch {
        /* ignore */
      }
      // Hard reload so every workspace-scoped fetch re-runs in the new mode.
      window.location.assign("/dashboard");
      return next;
    });
  }, []);

  const refreshBilling = useCallback(async () => {
    try {
      const r = await api.get("/billing/summary");
      setBilling(r.data);
      return r.data;
    } catch {
      setBilling(null);
      return null;
    }
  }, []);

  const refreshWorkspaces = useCallback(async () => {
    try {
      const r = await api.get("/workspaces");
      const list = r.data?.workspaces || [];
      setWorkspaces(list);
      // Validate the persisted active id against membership; reset to the
      // default workspace when it's stale (e.g. a previous user's id).
      let stored = null;
      try {
        stored = localStorage.getItem("active_workspace_id");
      } catch {
        stored = null;
      }
      const valid = list.find((w) => w.id === stored);
      if (!valid) {
        const def = list.find((w) => w.is_default) || list[0];
        if (def) {
          try {
            localStorage.setItem("active_workspace_id", def.id);
          } catch {
            /* ignore */
          }
          setActiveWorkspaceId(def.id);
        }
      } else {
        setActiveWorkspaceId(stored);
      }
      return list;
    } catch {
      setWorkspaces([]);
      return [];
    }
  }, []);

  const switchWorkspace = useCallback((wid) => {
    try {
      localStorage.setItem("active_workspace_id", wid);
    } catch {
      /* ignore */
    }
    // Hard navigation so every page re-fetches under the new X-Workspace-ID.
    window.location.assign("/dashboard");
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      // Don't await - load billing + workspaces in the background.
      api
        .get("/billing/summary")
        .then((br) => setBilling(br.data))
        .catch(() => setBilling(null));
      refreshWorkspaces();
    } catch {
      setUser(null);
      setBilling(null);
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  }, [refreshWorkspaces]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    try {
      localStorage.removeItem("active_workspace_id");
    } catch {
      /* ignore */
    }
    setUser(null);
    setBilling(null);
    setWorkspaces([]);
    window.location.href = "/login";
  };

  const activeWorkspace =
    workspaces.find((w) => w.id === activeWorkspaceId) ||
    workspaces.find((w) => w.is_default) ||
    null;

  return (
    <AuthCtx.Provider
      value={{
        user,
        setUser,
        billing,
        loading,
        logout,
        checkAuth,
        refreshBilling,
        workspaces,
        activeWorkspace,
        activeWorkspaceId,
        refreshWorkspaces,
        switchWorkspace,
        enterpriseMode,
        toggleEnterpriseMode,
        isAdmin: !!user?.is_admin,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
